import { useParams, useSearchParams } from '@mentimeter/next-navigation';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';

interface IdStore {
  slideDeckPublicKey: string;
  slideAdminKey: string | undefined;
  getSlideAdminKey: () => string | undefined;
  getSlideDeckPublicKey: () => string;
  /**
   * Sets the current `slideDeckPublicKey`. NOTE: Do not use this to try to navigate to a slide deck
   */
  setSlideDeckPublicKey: (slideDeckPublicKey: string) => void;
  /**
   * Sets the current `slideAdminKey`. NOTE: Do not use this to try to navigate to a slide
   */
  setSlideAdminKey: (slideAdminKey: string | undefined) => void;
}

export const useIdStore = create<IdStore>((set, get) => ({
  slideDeckPublicKey: '',
  slideAdminKey: undefined,
  getSlideAdminKey: () => get().slideAdminKey,
  getSlideDeckPublicKey: () => get().slideDeckPublicKey,
  setSlideDeckPublicKey: (slideDeckPublicKey) => set({ slideDeckPublicKey }),
  setSlideAdminKey: (slideAdminKey) => set({ slideAdminKey }),
}));

export const useActiveSlideDeckPublicKey = () =>
  useIdStore((state) => state.slideDeckPublicKey);
export const useActiveSlideAdminKey = (): string =>
  useIdStore((state) => state.slideAdminKey) as string;

export const useAppRouterIds = (ids?: {
  slideDeckPublicKey?: string | undefined;
  slideAdminKey?: string | undefined;
}) => {
  const mounted = useRef(false);
  const params = useParams();
  const search = useSearchParams();
  let slideDeckPublicKey: string | undefined = ids?.slideDeckPublicKey;
  let slideAdminKey: string | undefined = ids?.slideAdminKey;

  if (params.slideDeckPublicKey && search.get('question')) {
    slideDeckPublicKey = params.slideDeckPublicKey as string;
    slideAdminKey = search.get('question') as string;
  }

  const setSlideDeckPublicKey = useIdStore(
    (state) => state.setSlideDeckPublicKey,
  );
  const setSlideAdminKey = useIdStore((state) => state.setSlideAdminKey);

  if (slideDeckPublicKey && !mounted.current) {
    setSlideDeckPublicKey(slideDeckPublicKey);
    setSlideAdminKey(slideAdminKey);
  }

  useEffect(() => {
    if (slideDeckPublicKey) {
      setSlideDeckPublicKey(slideDeckPublicKey);
    }
    setSlideAdminKey(slideAdminKey);
    mounted.current = true;
  }, [
    slideAdminKey,
    slideDeckPublicKey,
    setSlideAdminKey,
    setSlideDeckPublicKey,
  ]);

  return { slideDeckPublicKey, slideAdminKey };
};
